// KnowledgeFeedBoard.tsx — the YouTube knowledge app (Board App #1, redesigned
// 2026-07-24 with Erez). A master/detail over the watched-video corpus, sized at
// web-normal dimensions so hover-to-frame reads like a real page.
//
//   LEFT  — a scannable table: big thumbnail, big title, channel + tags.
//   RIGHT — a knowledge PAGE for the selected video: a sane-sized player + facts
//           on top, then structured sections mined from the transcript —
//           About · Key points · Insights & learnings · Action items · Transcript.
//
// Content comes from youtube_watch (summary/about, tags, principles=insights,
// key_points, action_items) via /api/knowledge-feed[/:id]. Enrichment runs as a
// batch (Gemini over transcripts); rows without it degrade gracefully.
//
// React-Flow trap: a non-draggable node gets pointer-events:none, killing clicks
// AND wheel. Every interactive/scroll element carries pointerEvents:"auto" +
// nowheel/nodrag/nopan, and buttons stop pointerdown from starting a pan.

import { useEffect, useMemo, useState } from "react";
import {
  Loader2, Tag, Youtube, ListChecks, Lightbulb, Sparkles,
  ExternalLink, FileText, ChevronDown, PlayCircle, Clock, CalendarDays,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/api";

// ── palette — clean editorial (skill://design): light, one accent ─────────────
const T = {
  ink: "#191D1B", ink2: "#55605A", faint: "#98A099",
  paper: "#FAF9F6", card: "#FFFFFF", bone: "#F3F0EA",
  line: "#EAE7E0", hair: "#E2DED6",
  accent: "#2E6B52", accentSoft: "#EAF2ED", accentLine: "#CDE1D6",
  amber: "#B07A1E", amberSoft: "#FBF3E3",
  red: "#C0392B",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Row = {
  video_id: string;
  title: string | null;
  channel: string | null;
  url: string | null;
  duration_sec: number | null;
  is_short: boolean | null;
  watched_pull_at: string;
  has_transcript: boolean;
  summary: string | null;
  tags: string[] | null;
  topic: string | null;
  is_ai_related: boolean | null;
  is_substantive: boolean | null;
  kb_page_slug: string | null;
  ingest_error: string | null;
};
type Insight = { title: string; snippet: string };
type Detail = Row & {
  transcript: string | null;
  principles: Insight[] | null;
  key_points: string[] | null;
  action_items: string[] | null;
};

type Filter = "videos" | "shorts" | "all" | "ai" | "errors";

const thumb = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

function fmtDur(sec: number | null): string {
  if (!sec || sec <= 0) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const clamp = (n: number): React.CSSProperties => ({
  display: "-webkit-box", WebkitLineClamp: n, WebkitBoxOrient: "vertical", overflow: "hidden",
});

// Videos first and selected by default — long-form is what Erez actually reads;
// shorts are ~2/3 of the raw history and were drowning the list. "Substantive"
// was removed on his instruction (2026-08-14): it exposed the internal
// is_substantive flag under a label nobody could interpret from the outside.
const FILTERS: { id: Filter; label: string; predicate: (r: Row) => boolean }[] = [
  { id: "videos", label: "Videos", predicate: (r) => r.is_short !== true },
  { id: "shorts", label: "Shorts", predicate: (r) => r.is_short === true },
  { id: "all", label: "All", predicate: () => true },
  { id: "ai", label: "AI", predicate: (r) => r.is_ai_related === true },
  { id: "errors", label: "Errors", predicate: (r) => !!r.ingest_error },
];

// ── one row in the left table ─────────────────────────────────────────────────
function ListRow({ r, active, onClick }: { r: Row; active: boolean; onClick: () => void }) {
  const [err, setErr] = useState(false);
  const dur = fmtDur(r.duration_sec);
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      className="nodrag nopan"
      style={{
        display: "flex", gap: 18, width: "100%", textAlign: "left", cursor: "pointer",
        pointerEvents: "auto", fontFamily: FONT, padding: 16, marginBottom: 12,
        border: `1px solid ${active ? T.accent : T.hair}`,
        background: active ? T.accentSoft : T.card,
        borderRadius: 16,
        boxShadow: active ? `0 0 0 1px ${T.accent}` : "0 1px 3px rgba(25,29,27,0.04)",
      }}
    >
      <div style={{ position: "relative", width: 224, flexShrink: 0, aspectRatio: "16 / 9", borderRadius: 12, overflow: "hidden", background: T.bone }}>
        {err ? (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: T.faint }}>NO IMG</div>
        ) : (
          <img src={thumb(r.video_id)} alt="" onError={() => setErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        )}
        {dur ? (
          <span style={{ position: "absolute", right: 7, bottom: 7, background: "rgba(0,0,0,0.8)", color: "#fff", fontSize: 12.5, fontWeight: 600, padding: "2px 7px", borderRadius: 5 }}>{dur}</span>
        ) : null}
      </div>
      <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ ...clamp(2), fontSize: 18, fontWeight: 700, color: T.ink, lineHeight: 1.28, letterSpacing: "-0.01em" }}>{r.title ?? r.video_id}</div>
        {r.summary ? (
          <div style={{ ...clamp(2), fontSize: 14, color: T.ink2, lineHeight: 1.5, marginTop: 7 }}>{r.summary.replace(/\s+/g, " ").trim()}</div>
        ) : r.ingest_error ? (
          <div style={{ ...clamp(2), fontSize: 13, color: T.red, lineHeight: 1.5, marginTop: 7 }}>{r.ingest_error}</div>
        ) : (
          <div style={{ fontSize: 13, color: T.faint, fontStyle: "italic", marginTop: 7 }}>Not enriched yet</div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center", marginTop: "auto", paddingTop: 10 }}>
          {r.channel && r.channel !== "NA" ? (
            <span style={{ fontSize: 12.5, color: T.ink2, fontWeight: 600 }}>{r.channel}</span>
          ) : null}
          {(r.tags ?? []).slice(0, 3).map((t) => (
            <span key={t} style={{ fontSize: 11.5, color: T.ink2, background: T.bone, border: `1px solid ${T.line}`, padding: "2px 9px", borderRadius: 999 }}>{t}</span>
          ))}
        </div>
      </div>
    </button>
  );
}

// ── a section heading in the detail page ──────────────────────────────────────
function SectionHead({ icon: Icon, text, tone = T.accent }: { icon: React.ComponentType<{ style?: React.CSSProperties }>; text: string; tone?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "30px 0 14px" }}>
      <Icon style={{ width: 19, height: 19, color: tone }} />
      <h2 style={{ fontSize: 15, fontWeight: 800, color: T.ink, margin: 0, textTransform: "uppercase", letterSpacing: "0.08em" }}>{text}</h2>
      <div style={{ flex: 1, height: 1, background: T.line }} />
    </div>
  );
}

// ── the right detail page ─────────────────────────────────────────────────────
function DetailPane({ videoId }: { videoId: string | null }) {
  const api = useApi();
  const [row, setRow] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!videoId) return;
    setRow(null); setError(null); setShowTranscript(false);
    (async () => {
      try {
        const res = await api(`/api/knowledge-feed/${videoId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Detail;
        if (!cancelled) setRow(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  if (!videoId) return <div style={{ padding: 48, color: T.faint, fontSize: 16, fontFamily: FONT }}>Pick a video on the left.</div>;
  if (error) return <div style={{ padding: 48, color: T.red, fontSize: 16, fontFamily: FONT }}>Failed to load — {error}</div>;
  if (!row)
    return (
      <div style={{ padding: 48, color: T.faint, fontSize: 16, fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 10 }}>
        <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} /> Loading…
      </div>
    );

  const watch = row.url ?? `https://www.youtube.com/watch?v=${row.video_id}`;
  const dur = fmtDur(row.duration_sec);
  const insights = Array.isArray(row.principles) ? row.principles : [];
  const keyPoints = Array.isArray(row.key_points) ? row.key_points : [];
  const actions = Array.isArray(row.action_items) ? row.action_items : [];

  return (
    <div style={{ padding: "36px 44px 52px", fontFamily: FONT }}>
      {/* header */}
      <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", color: T.accent, textTransform: "uppercase", marginBottom: 12 }}>Knowledge Feed · YouTube</div>
      <h1 style={{ fontSize: 32, fontWeight: 800, color: T.ink, margin: "0 0 14px", letterSpacing: "-0.025em", lineHeight: 1.16 }}>{row.title ?? row.video_id}</h1>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center", fontSize: 14, color: T.ink2, marginBottom: 24 }}>
        {row.channel && row.channel !== "NA" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 600 }}><Youtube style={{ width: 16, height: 16, color: T.red }} />{row.channel}</span>
        ) : null}
        {dur ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.faint }}><Clock style={{ width: 14, height: 14 }} />{dur}</span> : null}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.faint }}><CalendarDays style={{ width: 14, height: 14 }} />{new Date(row.watched_pull_at).toLocaleDateString()}</span>
        {row.topic ? <span style={{ fontSize: 11.5, fontWeight: 700, color: T.accent, background: T.accentSoft, border: `1px solid ${T.accentLine}`, padding: "2px 11px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.05em" }}>{row.topic}</span> : null}
      </div>

      {/* TOP — video (left) + facts card (right) */}
      <div style={{ display: "flex", gap: 24, alignItems: "stretch", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 560px", minWidth: 420 }}>
          <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: T.bone, border: `1px solid ${T.line}`, borderRadius: 14, overflow: "hidden" }}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${row.video_id}`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="nodrag nopan"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          </div>
        </div>
        {/* facts + tags card */}
        <div style={{ flex: "1 1 320px", minWidth: 280, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 14, padding: "22px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <a href={watch} target="_blank" rel="noreferrer" className="nodrag nopan" onPointerDown={(e) => e.stopPropagation()}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 600, color: "#fff", background: T.red, border: `1px solid ${T.red}`, padding: "9px 15px", borderRadius: 10, textDecoration: "none", pointerEvents: "auto" }}>
              <PlayCircle style={{ width: 16, height: 16 }} /> Watch <ExternalLink style={{ width: 12, height: 12, opacity: 0.85 }} />
            </a>
            {row.kb_page_slug ? (
              <Link to={`/${row.kb_page_slug.replace(/^\/+/, "")}`} className="nodrag nopan" onPointerDown={(e) => e.stopPropagation()}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 600, color: T.ink, background: T.card, border: `1px solid ${T.hair}`, padding: "9px 15px", borderRadius: 10, textDecoration: "none", pointerEvents: "auto" }}>
                <FileText style={{ width: 15, height: 15 }} /> KB page
              </Link>
            ) : null}
          </div>
          <div style={{ height: 1, background: T.line }} />
          <div>
            <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: T.faint, textTransform: "uppercase", marginBottom: 10 }}>Tags</div>
            {(row.tags ?? []).length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {(row.tags ?? []).map((t) => (
                  <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: T.ink2, background: T.bone, border: `1px solid ${T.line}`, padding: "3px 10px", borderRadius: 999 }}>
                    <Tag style={{ width: 10, height: 10, color: T.faint }} />{t}
                  </span>
                ))}
              </div>
            ) : <div style={{ fontSize: 13, color: T.faint, fontStyle: "italic" }}>No tags yet.</div>}
          </div>
        </div>
      </div>

      {/* ABOUT */}
      <SectionHead icon={Sparkles} text="What it's about" />
      {row.summary ? (
        <div style={{ fontSize: 16.5, lineHeight: 1.7, color: T.ink, whiteSpace: "pre-wrap" }}>{row.summary}</div>
      ) : (
        <div style={{ fontSize: 15, color: T.faint, fontStyle: "italic" }}>Not enriched yet — no summary.</div>
      )}

      {/* KEY POINTS */}
      {keyPoints.length > 0 && (
        <>
          <SectionHead icon={ListChecks} text="Key points" tone={T.ink} />
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            {keyPoints.map((k, i) => (
              <div key={i} style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                <span style={{ marginTop: 8, width: 7, height: 7, borderRadius: 999, background: T.accent, flexShrink: 0 }} />
                <div style={{ fontSize: 15.5, lineHeight: 1.6, color: T.ink }}>{k}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* INSIGHTS & LEARNINGS */}
      {insights.length > 0 && (
        <>
          <SectionHead icon={Lightbulb} text="Insights & learnings" tone={T.amber} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {insights.map((p, i) => (
              <div key={i} style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 12, padding: "16px 20px" }}>
                <div style={{ fontWeight: 700, fontSize: 15.5, color: T.ink, marginBottom: 6, lineHeight: 1.3 }}>{p.title}</div>
                <div style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.6 }}>{p.snippet}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ACTION ITEMS */}
      {actions.length > 0 && (
        <>
          <SectionHead icon={ListChecks} text="Potential action items" tone={T.accent} />
          <div style={{ background: T.accentSoft, border: `1px solid ${T.accentLine}`, borderRadius: 14, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
            {actions.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ marginTop: 2, width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${T.accent}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: T.accent }}>{i + 1}</span>
                <div style={{ fontSize: 15.5, lineHeight: 1.6, color: T.ink, fontWeight: 500 }}>{a}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* TRANSCRIPT — collapsible */}
      {row.transcript ? (
        <>
          <SectionHead icon={FileText} text="Transcript" tone={T.faint} />
          <button
            type="button"
            onClick={() => setShowTranscript((s) => !s)}
            onPointerDown={(e) => e.stopPropagation()}
            className="nodrag nopan"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: FONT, fontSize: 14, fontWeight: 600, color: T.ink2, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 10, padding: "9px 15px", cursor: "pointer", pointerEvents: "auto" }}
          >
            <ChevronDown style={{ width: 15, height: 15, transform: showTranscript ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
            {showTranscript ? "Hide transcript" : "Show full transcript"}
          </button>
          {showTranscript ? (
            <div className="nowheel nodrag nopan" style={{ marginTop: 14, background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: "20px 24px", maxHeight: 520, overflowY: "auto", color: T.ink2, fontSize: 14.5, lineHeight: 1.75, whiteSpace: "pre-wrap", pointerEvents: "auto" }}>{row.transcript}</div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ── the master/detail board panel ─────────────────────────────────────────────
export function KnowledgeFeedBoard() {
  const api = useApi();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("videos");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/knowledge-feed");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Row[];
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const pred = FILTERS.find((f) => f.id === filter)?.predicate ?? (() => true);
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (!pred(r)) return false;
      if (!q) return true;
      const hay = [r.title, r.channel, r.summary, r.topic, r.video_id, (r.tags ?? []).join(" ")].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, query]);

  const counts = useMemo(() => {
    const m: Record<Filter, number> = { videos: 0, shorts: 0, all: 0, ai: 0, errors: 0 };
    (rows ?? []).forEach((r) => {
      m.all++;
      if (r.is_short) m.shorts++;
      else m.videos++;
      if (r.is_ai_related) m.ai++;
      if (r.ingest_error) m.errors++;
    });
    return m;
  }, [rows]);

  useEffect(() => {
    if (!filtered.length) return;
    if (!selected || !filtered.some((r) => r.video_id === selected)) setSelected(filtered[0].video_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const LIST_W = 660;
  const H = 1160;

  return (
    <div className="nopan nodrag" style={{ display: "flex", gap: 26, width: LIST_W + 1320 + 26, fontFamily: FONT, alignItems: "stretch", pointerEvents: "auto" }}>
      {/* LEFT — the table */}
      <div
        className="nowheel nodrag nopan"
        style={{ width: LIST_W, height: H, overflow: "auto", background: T.paper, border: `1px solid ${T.hair}`, borderRadius: 20, boxShadow: "0 8px 24px rgba(25,29,27,0.05)", padding: "24px 24px 28px", pointerEvents: "auto" }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>Knowledge Feed</span>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: T.accent }}>{counts.all} videos</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {FILTERS.map((f) => {
            const active = f.id === filter;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                onPointerDown={(e) => e.stopPropagation()}
                className="nodrag nopan"
                style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: active ? "#fff" : T.ink2, background: active ? T.accent : T.card, border: `1px solid ${active ? T.accent : T.hair}`, borderRadius: 999, padding: "7px 15px", cursor: "pointer", pointerEvents: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                {f.label}
                <span style={{ fontSize: 12, fontWeight: 700, color: active ? "#fff" : T.faint }}>{counts[f.id]}</span>
              </button>
            );
          })}
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          placeholder="Search title, channel, summary, tags…"
          className="nodrag nopan"
          style={{ fontFamily: FONT, fontSize: 14.5, color: T.ink, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 10, padding: "11px 15px", width: "100%", boxSizing: "border-box", outline: "none", marginBottom: 18, pointerEvents: "auto" }}
        />

        {error ? (
          <div style={{ padding: 18, color: T.red, fontSize: 14 }}>Failed to load feed: {error}</div>
        ) : rows === null ? (
          <div style={{ padding: 18, color: T.faint, fontSize: 15, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Loader2 style={{ width: 15, height: 15, animation: "spin 1s linear infinite" }} /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 18, color: T.faint, fontSize: 15 }}>No matches.</div>
        ) : (
          filtered.map((r) => <ListRow key={r.video_id} r={r} active={r.video_id === selected} onClick={() => setSelected(r.video_id)} />)
        )}
      </div>

      {/* RIGHT — the knowledge page */}
      <div
        className="nowheel nodrag nopan"
        style={{ flex: 1, height: H, overflow: "auto", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, boxShadow: "0 8px 24px rgba(25,29,27,0.05)", pointerEvents: "auto" }}
      >
        <DetailPane videoId={selected} />
      </div>
    </div>
  );
}

export default KnowledgeFeedBoard;
